import React, { FC, useMemo } from 'react';

import type { ColDef, ICellRendererParams } from 'ag-grid-community';

import AGTable from 'components/ag-table/AGTable';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';

import formatDate from 'helpers/dateTimeHelpers';

import { FormulaBaseModel } from '../../../../models/formula.model';

interface Step3ReviewProps {
    selectedMessages: FormulaBaseModel[];
}

const getDisplayStatus = (status: string): string => {
    return status === 'Approved' ? 'Live' : status;
};

const Step3Review: FC<Step3ReviewProps> = ({ selectedMessages }) => {
    const columns = useMemo<ColDef<FormulaBaseModel>[]>(() => {
        return [
            {
                headerName: 'Message group',
                field: 'name',
                resizable: true,
                flex: 1,
                cellRenderer: ({
                    data,
                }: ICellRendererParams<FormulaBaseModel>) => {
                    return data?.name || 'Untitled Message';
                },
            },
            {
                headerName: 'Status',
                field: 'status',
                minWidth: 120,
                cellRenderer: ({
                    data,
                }: ICellRendererParams<FormulaBaseModel>) => {
                    return getDisplayStatus(data?.status ?? '');
                },
            },
            {
                headerName: 'Reachable users',
                field: 'message_count',
                minWidth: 150,
                cellRenderer: ({
                    data,
                }: ICellRendererParams<FormulaBaseModel>) => {
                    return data?.message_count?.toLocaleString() ?? '0';
                },
            },
            {
                headerName: 'Updated',
                field: 'updated_at',
                minWidth: 120,
                cellRenderer: ({
                    data,
                }: ICellRendererParams<FormulaBaseModel>) => {
                    return formatDate(data?.updated_at ?? '');
                },
            },
        ];
    }, []);

    return (
        <Container>
            <Header>
                <Title>
                    Changing the email template will affect{' '}
                    {selectedMessages.length} message group
                    {selectedMessages.length !== 1 ? 's' : ''}
                </Title>
                <Description>
                    Once saved, any fields mapped to ‘none’ will lose their
                    existing content. This change will apply to the message
                    groups listed below.
                </Description>
            </Header>

            <TableContainer>
                <AGTable
                    columns={columns}
                    rowData={selectedMessages}
                    pagination={selectedMessages.length > 10}
                    isNoPaddingMargin
                    emptyStateText="No messages selected"
                />
            </TableContainer>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    height: '100%',
});

const Header = styled.div({
    display: 'flex',
    flexDirection: 'column',
    // gap: aampeTheme.spacing4(2),
    height: aampeTheme.spacing4(15),
    justifyContent: 'space-between',
});

const Title = styled.h3({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.heavy,
    color: aampeTheme.colors.neutral09,
    lineHeight: aampeTheme.size.lineHeight.small,

    margin: 0,
});

const Description = styled.p({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.neutral09,
    margin: 0,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
});

const TableContainer = styled.div({
    flex: 1,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
});

export default Step3Review;
